import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { Success } from "../../../../commons/Messages/Messages";
import { CATEGORY_LABELS } from "../belege/constants";
import { createSupplier, updateSupplier } from "./api";

const toForm = supplier => ({
  ...supplier,
  ibans: supplier.ibans?.length ? supplier.ibans : [],
  selfBillingAgreement: {
    agreed: Boolean(supplier.selfBillingAgreement?.agreed),
    date: supplier.selfBillingAgreement?.date ? dayjs(supplier.selfBillingAgreement.date) : null
  },
  bauabzugsteuer: {
    ...supplier.bauabzugsteuer,
    validFrom: supplier.bauabzugsteuer?.validFrom ? dayjs(supplier.bauabzugsteuer.validFrom) : null,
    validUntil: supplier.bauabzugsteuer?.validUntil
      ? dayjs(supplier.bauabzugsteuer.validUntil)
      : null
  }
});

const fromForm = values => ({
  ...values,
  ibans: (values.ibans || []).filter(entry => entry?.iban),
  selfBillingAgreement: {
    agreed: Boolean(values.selfBillingAgreement?.agreed),
    date: values.selfBillingAgreement?.date
      ? values.selfBillingAgreement.date.format("YYYY-MM-DD")
      : ""
  },
  bauabzugsteuer: {
    ...values.bauabzugsteuer,
    validFrom: values.bauabzugsteuer?.validFrom
      ? values.bauabzugsteuer.validFrom.format("YYYY-MM-DD")
      : "",
    validUntil: values.bauabzugsteuer?.validUntil
      ? values.bauabzugsteuer.validUntil.format("YYYY-MM-DD")
      : ""
  }
});

const NEW_SUPPLIER = {
  address: { country: "DE" },
  ibans: [],
  defaultTaxRate: 19,
  paymentTermsDays: 14,
  skonto: { percent: 0, days: 0 },
  is13bSupplier: false,
  bauabzugsteuer: { applies: false },
  selfBillingAgreement: { agreed: false, date: null }
};

/** Create/edit a supplier: master data, IBANs, § 13b and the Freistellungsbescheinigung. */
const SupplierModal = ({
  open,
  supplier,
  categories,
  accounts,
  bauleistender,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const is13b = Form.useWatch("is13bSupplier", form);
  const applies = Form.useWatch(["bauabzugsteuer", "applies"], form);
  const selfBilling = Form.useWatch(["selfBillingAgreement", "agreed"], form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(supplier ? toForm(supplier) : NEW_SUPPLIER);
  }, [open, supplier]);

  const save = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const body = fromForm(values);
      const res = supplier ? await updateSupplier(supplier._id, body) : await createSupplier(body);
      Success(t("Supplier saved"));
      onSaved(res.data.supplier);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={supplier ? `${supplier.name} (${supplier.supplierNumber})` : t("New supplier")}
      width={820}
      rootClassName="acct-module"
      destroyOnClose
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={saving}
      onOk={save}
    >
      <Form form={form} layout="vertical" size={isMobile ? "middle" : "small"}>
        <Row gutter={8}>
          <Col xs={24} md={16}>
            <Form.Item
              name="name"
              label={t("Name")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t("Creditor account")}>
              <Input value={supplier?.kreditorAccount || t("assigned on save")} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item name={["address", "street"]} label={t("Street")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={8} md={4}>
            <Form.Item name={["address", "zip"]} label={t("ZIP")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={16} md={5}>
            <Form.Item name={["address", "city"]} label={t("City")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={8} md={3}>
            <Form.Item name={["address", "country"]} label={t("Country")}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col xs={24} md={8}>
            <Form.Item name="email" label={t("Email")}>
              <Input type="email" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="phone" label={t("Phone")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="vatId" label={t("VAT ID")}>
              <Input placeholder="DE123456789" />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="taxNumber" label={t("Tax number")}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="start" plain>
          {t("Bank accounts")}
        </Divider>
        <Form.List name="ibans">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <Row gutter={8} key={key} align="middle">
                  <Col xs={24} md={11}>
                    <Form.Item
                      name={[name, "iban"]}
                      rules={[{ required: true, message: t("Required") }]}
                    >
                      <Input placeholder="IBAN" />
                    </Form.Item>
                  </Col>
                  <Col xs={10} md={5}>
                    <Form.Item name={[name, "bic"]}>
                      <Input placeholder="BIC" />
                    </Form.Item>
                  </Col>
                  <Col xs={10} md={6}>
                    <Form.Item name={[name, "holder"]}>
                      <Input placeholder={t("Account holder")} />
                    </Form.Item>
                  </Col>
                  <Col xs={4} md={2}>
                    <Form.Item>
                      <Button
                        type="text"
                        icon={<MinusCircleOutlined />}
                        aria-label={t("Delete")}
                        onClick={() => remove(name)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Button
                size={isMobile ? "middle" : "small"}
                icon={<PlusOutlined />}
                onClick={() => add({ iban: "", bic: "", holder: "" })}
              >
                {t("Add IBAN")}
              </Button>
            </>
          )}
        </Form.List>

        <Divider orientation="start" plain>
          {t("Booking defaults")}
        </Divider>
        <Row gutter={8}>
          <Col xs={24} md={8}>
            <Form.Item name="defaultCategory" label={t("Category")}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(categories || []).map(c => ({
                  value: c.key,
                  label: t(CATEGORY_LABELS[c.key] || c.label)
                }))}
                onChange={key => {
                  const c = (categories || []).find(x => x.key === key);
                  if (c) form.setFieldsValue({ defaultAccount: c.account });
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="defaultAccount" label={t("Account")}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(accounts || []).map(a => ({
                  value: a.number,
                  label: `${a.number} ${a.name}`
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="defaultTaxRate" label={t("Tax rate")}>
              <Select options={[19, 7, 0].map(r => ({ value: r, label: `${r} %` }))} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col xs={24} md={8}>
            <Form.Item name="paymentTermsDays" label={t("Payment terms (days)")}>
              <InputNumber min={0} max={365} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name={["skonto", "percent"]} label={t("Skonto %")}>
              <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name={["skonto", "days"]} label={t("Skonto days")}>
              <InputNumber min={0} max={120} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="start" plain>
          {t("Construction work (§ 13b UStG / § 48 EStG)")}
        </Divider>
        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item
              name="is13bSupplier"
              label={t("Bills construction work without VAT (§ 13b)")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name={["bauabzugsteuer", "applies"]}
              label={t("Subject to Bauabzugsteuer (§ 48 EStG)")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        {(is13b || applies) && (
          <>
            {!bauleistender && (
              <Alert
                type="info"
                showIcon
                className="mb-3"
                message={t(
                  "Reverse charge and Bauabzugsteuer only apply if your business itself performs construction work (accounting settings)."
                )}
              />
            )}
            <Row gutter={8}>
              <Col xs={24} md={8}>
                <Form.Item
                  name={["bauabzugsteuer", "freistellungNr"]}
                  label={t("Exemption certificate no.")}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={["bauabzugsteuer", "finanzamt"]} label={t("Tax office")}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item name={["bauabzugsteuer", "validFrom"]} label={t("Valid from")}>
                  <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item name={["bauabzugsteuer", "validUntil"]} label={t("Valid until")}>
                  <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Alert
              type="warning"
              showIcon
              message={t(
                "Without a valid exemption certificate, 15 % of every gross invoice is withheld and paid to the tax office."
              )}
            />
          </>
        )}
        <Divider orientation="start" plain>
          {t("Self-billing (Gutschriftverfahren, § 14 Abs. 2 UStG)")}
        </Divider>
        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item
              name={["selfBillingAgreement", "agreed"]}
              label={t("Agreement: we bill ourselves on the supplier's behalf")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          {selfBilling && (
            <Col xs={24} md={12}>
              <Form.Item
                name={["selfBillingAgreement", "date"]}
                label={t("Agreement date")}
                rules={[{ required: true, message: t("Required") }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          )}
        </Row>
        {selfBilling && (
          <Alert
            type="info"
            showIcon
            message={t(
              "A Gutschrift must name the supplier's address and tax number or VAT ID. The agreement must exist before the first Gutschrift."
            )}
          />
        )}
        <Form.Item name="notes" label={t("Notes")} style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SupplierModal;
